'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '@/components/ui/Modal';
import { Appointment, Technician } from './schedulingTypes';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  technicians: Technician[];
  preselectedDate: string | null;
  saturdaySlotsUsed: number;
  onSave: (appt: Appointment) => void;
}

interface FormData {
  customerNumber: string;
  customerName: string;
  address: string;
  landmark: string;
  machineModel: string;
  scheduledDate: string;
  scheduledTime: string;
  assignedTechnicianId: string;
  paymentMode: 'Cash' | 'Check' | 'BDO' | 'GCash';
  notes: string;
}

export default function CreateAppointmentModal({ open, onClose, technicians, preselectedDate, saturdaySlotsUsed, onSave }: Props) {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ sedi: false, pre: false, mem: false, post: false });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { scheduledDate: preselectedDate || '', scheduledTime: '09:00', paymentMode: 'Cash' },
  });

  const watchedDate = watch('scheduledDate');
  const isSaturdayDate = watchedDate ? new Date(watchedDate).getDay() === 6 : false;
  const saturdayAtLimit = isSaturdayDate && saturdaySlotsUsed >= 3;

  function toggleFilter(key: keyof typeof filters) {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function onSubmit(data: FormData) {
    if (saturdayAtLimit) {
      toast.error('Saturday booking limit reached (max 3 slots). Cannot create this appointment.');
      return;
    }
    setLoading(true);
    const tech = technicians.find((t) => t.id === data.assignedTechnicianId);
    // Backend: POST /api/appointments with form data
    setTimeout(() => {
      const appt: Appointment = {
        id: `appt-${Date.now()}`,
        customerNumber: data.customerNumber,
        customerName: data.customerName,
        address: data.address,
        landmark: data.landmark,
        machineModel: data.machineModel,
        scheduledDate: data.scheduledDate,
        scheduledTime: data.scheduledTime,
        assignedTechnicianId: data.assignedTechnicianId,
        assignedTechnicianName: tech?.name || '',
        paymentMode: data.paymentMode,
        filters,
        status: 'scheduled',
        isSaturday: isSaturdayDate,
        rebookingFee: isSaturdayDate ? 300 : 0,
        notes: data.notes,
      };
      onSave(appt);
      toast.success(`Appointment scheduled for ${data.customerName} on ${data.scheduledDate}`);
      reset();
      setFilters({ sedi: false, pre: false, mem: false, post: false });
      setLoading(false);
    }, 800);
  }

  return (
    <Modal open={open} onClose={onClose} title="Create New Appointment" size="lg">
      {isSaturdayDate && (
        <div className={`mb-4 rounded-lg p-3 flex items-start gap-2 text-sm ${saturdayAtLimit ? 'bg-red-50 border border-red-200 text-danger' : 'bg-amber-50 border border-amber-200 text-amber-700'}`}>
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            {saturdayAtLimit
              ? `Saturday booking limit reached (${saturdaySlotsUsed}/3). This appointment cannot be created.`
              : `Saturday booking: ₱300 rebooking fee will be applied. Slots used: ${saturdaySlotsUsed}/3.`}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Customer Number */}
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">
              Customer Number <span className="text-danger">*</span>
            </label>
            <p className="text-xs text-muted-foreground mb-1">Enter existing CUST-YYYY-XXX number</p>
            <input
              type="text"
              {...register('customerNumber', { required: 'Customer number is required' })}
              placeholder="CUST-2026-001"
              className="w-full px-3 py-2 text-sm font-mono bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.customerNumber && <p className="mt-1 text-xs text-danger">{errors.customerNumber.message}</p>}
          </div>

          {/* Customer Name */}
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">
              Customer Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              {...register('customerName', { required: 'Customer name is required' })}
              placeholder="Full name"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.customerName && <p className="mt-1 text-xs text-danger">{errors.customerName.message}</p>}
          </div>

          {/* Scheduled Date */}
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">
              Scheduled Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              {...register('scheduledDate', { required: 'Date is required' })}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.scheduledDate && <p className="mt-1 text-xs text-danger">{errors.scheduledDate.message}</p>}
          </div>

          {/* Time Slot */}
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">
              Scheduled Time <span className="text-danger">*</span>
            </label>
            <input
              type="time"
              {...register('scheduledTime', { required: 'Time is required' })}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.scheduledTime && <p className="mt-1 text-xs text-danger">{errors.scheduledTime.message}</p>}
          </div>

          {/* Assigned Technician */}
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">Assigned Technician</label>
            <select
              {...register('assignedTechnicianId')}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">— Unassigned —</option>
              {technicians.filter((t) => t.status === 'active').map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* Payment Mode */}
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">
              Payment Mode <span className="text-danger">*</span>
            </label>
            <select
              {...register('paymentMode', { required: 'Payment mode is required' })}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="Cash">Cash</option>
              <option value="Check">Check</option>
              <option value="BDO">BDO Bank Transfer</option>
              <option value="GCash">GCash</option>
            </select>
          </div>
        </div>

        {/* Address */}
        <div>
          <label className="block text-xs font-600 text-foreground mb-1">Service Address</label>
          <input
            type="text"
            {...register('address')}
            placeholder="House No., Street, Barangay, City"
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Landmark */}
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">Landmark</label>
            <input
              type="text"
              {...register('landmark')}
              placeholder="e.g. Near Jollibee, Blue gate"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Machine Model */}
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">Machine Model</label>
            <input
              type="text"
              {...register('machineModel')}
              placeholder="e.g. Leenistech RO-5S"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {/* Filter Checkboxes */}
        <div>
          <label className="block text-xs font-600 text-foreground mb-2">
            Filters to Replace
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Check filters scheduled for replacement — inventory will deduct automatically on completion
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            {(['sedi', 'pre', 'mem', 'post'] as const).map((f) => {
              const labels: Record<string, string> = {
                sedi: 'Sedi (Sediment)',
                pre: 'Pre (Pre-Carbon)',
                mem: 'Mem (Membrane)',
                post: 'Post (Post-Carbon)',
              };
              return (
                <label
                  key={`caf-${f}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors select-none text-sm font-600 ${
                    filters[f]
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'bg-card border-border text-foreground hover:bg-muted'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={filters[f]}
                    onChange={() => toggleFilter(f)}
                  />
                  {labels[f]}
                </label>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs font-600 text-foreground mb-1">Notes</label>
          <textarea
            {...register('notes')}
            rows={2}
            placeholder="Special instructions, access notes, customer requests..."
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-600 bg-card border border-border rounded-lg hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || saturdayAtLimit}
            className="flex-1 py-2.5 text-sm font-600 btn-primary rounded-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Scheduling...
              </>
            ) : (
              'Schedule Appointment'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}